import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseExpenses, {
    type AddCaseExpenseValue,
} from '../../../../hooks/useLegalCaseExpenses';
import useBeneficiaries from '../../../../hooks/useBeneficiaries';
import type { Expense, ExpenseSummary } from '../../../../types/LegalCase';
import LegalCaseExpenseForm from './LegalCaseExpenseForm';
import ExpenseCompositionBar from './ExpenseCompositionBar';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseExpensesProps {
    caseId: string;
    initialExpenses?: Expense[];
    initialSummary?: ExpenseSummary;
    // Escritório responsável do caso: pré-seleciona o beneficiário no form de nova despesa
    // (o usuário pode trocar). Ajuda de UX — a despesa segue independente.
    defaultBeneficiaryId?: string | null;
}

// Painel do agregado: barra de composição do custo por categoria (estilo Pretensão) + a lista
// dos valores por categoria com contagem. Extraído para fora do componente (diretriz: sem
// função dentro de função). O total vem em destaque na própria barra.
const SummaryCard = ({ summary }: { summary: ExpenseSummary }) => (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
        <ExpenseCompositionBar summary={summary} />
        {summary.byCategory.length > 0 && (
            <ul className="space-y-1 border-t border-gray-100 dark:border-slate-700 pt-2">
                {summary.byCategory.map((row) => (
                    <li key={row.category} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-300">
                            {row.categoryFormatted}
                            <span className="text-gray-400 dark:text-gray-500"> ({row.count})</span>
                        </span>
                        <span className="text-gray-700 dark:text-gray-200">{row.totalFormatted}</span>
                    </li>
                ))}
            </ul>
        )}
    </div>
);

// Uma linha de despesa. Extraída para fora do componente.
const ExpenseItem = ({
    expense,
    onRemove,
    disabled,
}: {
    expense: Expense;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {expense.amountFormatted || 'Valor a confirmar'}
                </span>
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                    {expense.categoryFormatted}
                </span>
                {expense.paidAt && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        Pago em {formatDate(expense.paidAt)}
                    </span>
                )}
            </div>
            {expense.beneficiaryName && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Beneficiário: {expense.beneficiaryName}
                </div>
            )}
            {expense.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">
                    {expense.description}
                </p>
            )}
        </div>
        <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            title="Remover despesa"
            className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
        >
            <FaTrash size={11} />
        </button>
    </li>
);

// Seção "Custos" do detalhe do caso. Lista as despesas do caso com o agregado calculado
// (total + quebra por categoria). A remoção passa por ConfirmDialog (padrão do módulo). O
// beneficiário é escolhido num select com "+ Novo" inline (registro global compartilhado).
const LegalCaseExpenses = ({
    caseId,
    initialExpenses,
    initialSummary,
    defaultBeneficiaryId,
}: LegalCaseExpensesProps) => {
    const expenses = useLegalCaseExpenses(caseId, initialExpenses, initialSummary);
    const beneficiaries = useBeneficiaries();
    const [showAddForm, setShowAddForm] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openAddForm = () => {
        expenses.clearErrors();
        beneficiaries.clearErrors();
        setShowAddForm(true);
    };

    const closeForm = () => {
        expenses.clearErrors();
        setShowAddForm(false);
    };

    const submitAdd = async (value: AddCaseExpenseValue): Promise<boolean> => {
        const ok = await expenses.addExpense(value);
        if (ok) closeForm();
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await expenses.removeExpense(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Custos do caso
                </h4>
                {!showAddForm && (
                    <button
                        type="button"
                        onClick={openAddForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Despesa
                    </button>
                )}
            </div>

            {expenses.expenses.length > 0 && <SummaryCard summary={expenses.summary} />}

            {expenses.expenses.length === 0 && !showAddForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma despesa registrada</p>
            )}

            {expenses.expenses.length > 0 && (
                <ul>
                    {expenses.expenses.map((expense) => (
                        <ExpenseItem
                            key={expense.id}
                            expense={expense}
                            onRemove={() => setPendingRemoval(expense.id)}
                            disabled={expenses.isSaving || showAddForm}
                        />
                    ))}
                </ul>
            )}

            {showAddForm && (
                <LegalCaseExpenseForm
                    categoryOptions={expenses.categoryOptions}
                    beneficiaries={beneficiaries.beneficiaries}
                    beneficiaryTypeOptions={expenses.beneficiaryTypeOptions}
                    onCreateBeneficiary={beneficiaries.addBeneficiary}
                    beneficiaryErrorFields={beneficiaries.errorFields}
                    isBeneficiarySaving={beneficiaries.isSaving}
                    defaultBeneficiaryId={defaultBeneficiaryId || undefined}
                    errorFields={expenses.errorFields}
                    isSaving={expenses.isSaving}
                    onSubmit={submitAdd}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover despesa"
                message="Remover esta despesa? Esta ação não pode ser desfeita."
                isBusy={expenses.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseExpenses;
